import { useState, useEffect } from 'react';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { Link, useNavigate } from 'react-router-dom';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import TextField from '@mui/material/TextField';
import MetaData from '../Layouts/MetaData';
import BackdropLoader from '../Layouts/BackdropLoader';

const NewUser = () => {
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();

    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [gender, setGender] = useState("");
    const [hasAdminAccess, setHasAdminAccess] = useState(false);
    const [role, setRole] = useState("user");
    const [adminRole, setAdminRole] = useState("");
    const [allRoles, setAllRoles] = useState([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const fetchRoles = async () => {
            try {
                const { data } = await axios.get('/api/v1/admin/roles');
                setAllRoles(data.roles);
            } catch (error) {
                console.error('Failed to load roles', error);
            }
        };
        fetchRoles();
    }, []);

    const toggleAdminAccess = (checked) => {
        setHasAdminAccess(checked);
        setRole(checked ? "staff" : "user");
        if (!checked) setAdminRole("");
    };

    const submitHandler = async (e) => {
        e.preventDefault();

        if (!name.trim() || !email.trim() || !password) {
            return enqueueSnackbar("Name, email, and password are required", { variant: "warning" });
        }
        if (password.length < 8) {
            return enqueueSnackbar("Password must be at least 8 characters", { variant: "warning" });
        }
        if (hasAdminAccess && role === "staff" && !adminRole) {
            return enqueueSnackbar("Please pick a Staff Role, or create one first under Role Management", { variant: "warning" });
        }

        setSaving(true);
        try {
            await axios.post('/api/v1/admin/user/new', {
                name,
                email,
                password,
                gender,
                role,
                adminRole: role === "staff" ? adminRole : undefined,
            });
            enqueueSnackbar("User created", { variant: "success" });
            navigate("/admin/users");
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to create user", { variant: "error" });
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <MetaData title="Admin: New User | 1313 Mart" />
            {saving && <BackdropLoader />}

            <div className="flex flex-col bg-white shadow-lg rounded-lg mx-auto w-lg max-w-xl">
                <h2 className="text-center text-2xl font-medium mt-6 text-gray-800">New User</h2>

                <form onSubmit={submitHandler} className="p-5 sm:p-10">
                    <div className="flex flex-col gap-3 items-start">

                        <div className="flex flex-col w-full justify-between sm:flex-col gap-3 items-center">
                            <TextField
                                fullWidth
                                label="Full Name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                required
                            />
                            <TextField
                                fullWidth
                                label="Email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                            />
                            <TextField
                                fullWidth
                                label="Password"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                helperText="At least 8 characters — share this with the user so they can log in and change it"
                                required
                            />
                        </div>

                        <div className="flex gap-4 items-center">
                            <h2 className="text-md">Gender:</h2>
                            <div className="flex items-center gap-6">
                                <RadioGroup row value={gender} onChange={(e) => setGender(e.target.value)}>
                                    <FormControlLabel value="male" control={<Radio />} label="Male" />
                                    <FormControlLabel value="female" control={<Radio />} label="Female" />
                                </RadioGroup>
                            </div>
                        </div>

                        <div className="w-full border-t pt-3">
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={hasAdminAccess}
                                        onChange={(e) => toggleAdminAccess(e.target.checked)}
                                    />
                                }
                                label="Give this user access to the Admin Panel"
                            />
                            <p className="text-xs text-gray-400 -mt-1 mb-2">
                                Leave unchecked for a regular customer account. Only accounts with this
                                checked can ever log in to the admin panel.
                            </p>
                        </div>

                        {hasAdminAccess && (
                            <TextField
                                label="Admin Panel Role"
                                select
                                fullWidth
                                variant="outlined"
                                required
                                value={role}
                                onChange={(e) => setRole(e.target.value)}
                            >
                                <MenuItem value={"staff"}>Staff (custom access)</MenuItem>
                                <MenuItem value={"admin"}>Admin (full access)</MenuItem>
                            </TextField>
                        )}

                        {hasAdminAccess && role === "staff" && (
                            <TextField
                                label="Staff Role"
                                select
                                fullWidth
                                variant="outlined"
                                required
                                value={adminRole}
                                onChange={(e) => setAdminRole(e.target.value)}
                                helperText={allRoles.length === 0 ? "No roles created yet — go to Role Management first" : "Controls which admin pages this user can access"}
                            >
                                {allRoles.map((r) => (
                                    <MenuItem value={r._id} key={r._id}>{r.name}</MenuItem>
                                ))}
                            </TextField>
                        )}

                        <p className="text-xs text-gray-400">
                            A profile picture will be generated automatically from their name — they can change it later from their account.
                        </p>

                        <button type="submit" className="text-white py-3 w-full bg-primary-orange shadow hover:shadow-lg rounded-sm font-medium">Create User</button>
                        <Link className="hover:bg-gray-100 text-primary-blue text-center py-3 w-full shadow border rounded-sm font-medium" to="/admin/users">Cancel</Link>
                    </div>
                </form>
            </div>
        </>
    );
};

export default NewUser;